import React, { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  Upload,
  Image as ImageIcon,
  Video,
  File,
  X,
  Download,
  Trash2,
  Search,
  Filter,
  Grid3X3,
  List,
  Eye,
  Copy,
  MoreVertical,
  Plus,
  FolderOpen,
  AlertCircle
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import ConfirmationModal from '../common/ConfirmationModal'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'

const MediaLibrary = () => {
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState('all') // 'all', 'images', 'videos', 'documents'
  const [selectedFiles, setSelectedFiles] = useState([])
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [fileToDelete, setFileToDelete] = useState(null)
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)
  
  const fileInputRef = useRef(null)
  const dropZoneRef = useRef(null)
  const queryClient = useQueryClient()

  // Fetch media files
  const { data: mediaResponse, isLoading, error } = useQuery(
    ['media', searchQuery, filterType],
    () => apiService.get('/media', {
      params: { search: searchQuery, type: filterType }
    }).then(res => res.data),
    {
      staleTime: 30 * 1000, // 30 seconds (shorter for testing)
      retry: 1,
      refetchOnMount: true, // Ensure fresh data on mount
      refetchOnWindowFocus: true // Refetch when window gains focus
    }
  )

  // Ensure mediaFiles is always an array - fix for proper API response structure
  const mediaFiles = mediaResponse?.media || []

  // Upload mutation
  const uploadMutation = useMutation(
    (formData) => apiService.post('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        setUploadProgress(prev => ({ ...prev, uploading: percentCompleted }))
      }
    }),
    {
      onSuccess: () => {
        toast.success('Files uploaded successfully!')
        // Fix React Query cache invalidation
        queryClient.invalidateQueries(['media'])
        queryClient.refetchQueries(['media'])
        setShowUploadModal(false)
        setUploadProgress({})
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Upload failed')
        setUploadProgress({})
      }
    }
  )

  // Delete mutation
  const deleteMutation = useMutation(
    (mediaId) => apiService.delete(`/media/${mediaId}`),
    {
      onSuccess: () => {
        toast.success('File deleted successfully!')
        // Fix React Query cache invalidation
        queryClient.invalidateQueries(['media'])
        queryClient.refetchQueries(['media'])
        setSelectedFiles([])
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Delete failed')
      }
    }
  )

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files)
    if (files.length > 0) {
      handleFileUpload(files)
    }
    // Reset the input value to allow selecting the same file again
    event.target.value = ''
  }

  const handleDrop = (event) => {
    event.preventDefault()
    setDragOver(false)
    const files = Array.from(event.dataTransfer.files)
    handleFileUpload(files)
  }

  const handleFileUpload = (files) => {
    if (files.length === 0) return

    const formData = new FormData()
    files.forEach(file => {
      formData.append('files', file)
    })

    setUploadProgress({ uploading: 0 })
    uploadMutation.mutate(formData)
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (event) => {
    event.preventDefault()
    setDragOver(false)
  }

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return ImageIcon
    if (mimeType?.startsWith('video/')) return Video
    return File
  }

  const getFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url)
    toast.success('URL copied to clipboard!')
  }

  const filteredFiles = mediaFiles.filter(file => {
    const matchesSearch = file.original_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         file.alt_text?.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesType = filterType === 'all' || 
                       (filterType === 'images' && file.mime_type?.startsWith('image/')) ||
                       (filterType === 'videos' && file.mime_type?.startsWith('video/')) ||
                       (filterType === 'documents' && !file.mime_type?.startsWith('image/') && !file.mime_type?.startsWith('video/'))
    
    return matchesSearch && matchesType
  })

  if (error) {
    return (
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardContent className="text-center py-12">
          <FolderOpen className="h-16 w-16 text-neutral-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-2">
            Unable to load media library
          </h3>
          <p className="text-neutral-500 dark:text-neutral-400">
            {error.response?.data?.error || 'There was an error loading your files.'}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
            Media Library
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Manage your images, videos, and documents
          </p>
        </div>
        
        <Button onClick={() => setShowUploadModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Upload Files
        </Button>
      </div>

      {/* Toolbar */}
      <Card className="p-4 border-neutral-200 dark:border-[#2f2b41]">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 w-64 px-3 border border-neutral-200 rounded-md bg-white text-neutral-900 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:border-[#2f2b41] dark:text-neutral-100 dark:placeholder:text-neutral-400"
              />
            </div>

            {/* Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="h-9 w-32 pl-3 pr-8 py-2 border border-neutral-200 rounded-md bg-white text-neutral-900 text-sm font-medium text-left focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:border-[#2f2b41] dark:text-neutral-100 appearance-none cursor-pointer"
            >
              <option value="all">All Files</option>
              <option value="images">Images</option>
              <option value="videos">Videos</option>
              <option value="documents">Documents</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-neutral-200 dark:border-[#2f2b41]">
              <Button
                onClick={() => setViewMode('grid')}
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-r-none"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => setViewMode('list')}
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-l-none"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            {/* Selection Actions */}
            {selectedFiles.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  {selectedFiles.length} selected
                </span>
                <Button
                  onClick={() => setShowBulkDeleteConfirm(true)}
                  variant="destructive"
                  size="sm"
                  disabled={deleteMutation.isLoading}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Content */}
      {error ? (
        <Card>
          <CardContent className="text-center py-12">
            <div className="text-error-500 mb-4">
              <AlertCircle className="h-16 w-16 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-2">
                Error Loading Media
              </h3>
              <p className="text-neutral-500 dark:text-neutral-400">
                {error.response?.data?.error || 'Failed to load media files'}
              </p>
            </div>
            <Button
              onClick={() => {
                queryClient.invalidateQueries(['media'])
                queryClient.refetchQueries(['media'])
              }}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <Card className="border-neutral-200 dark:border-[#2f2b41]">
          <CardContent className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" text="Loading media files..." />
          </CardContent>
        </Card>
      ) : filteredFiles.length === 0 ? (
        <Card className="border-neutral-200 dark:border-[#2f2b41]">
          <CardContent className="text-center py-12">
            <FolderOpen className="h-16 w-16 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-2">
              {searchQuery || filterType !== 'all' ? 'No files found' : 'No files uploaded yet'}
            </h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-6">
              {searchQuery || filterType !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by uploading your first file.'
              }
            </p>
            <Button
              onClick={() => setShowUploadModal(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Upload Files
            </Button>
          </CardContent>
        </Card>
      ) : (
        // File Grid/List
        <div className={cn(
          viewMode === 'grid' 
            ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4'
            : 'space-y-2'
        )}>
          {filteredFiles.map((file) => (
            <MediaFileCard
              key={file.id}
              file={file}
              viewMode={viewMode}
              isSelected={selectedFiles.includes(file.id)}
              onSelect={(selected) => {
                if (selected) {
                  setSelectedFiles([...selectedFiles, file.id])
                } else {
                  setSelectedFiles(selectedFiles.filter(id => id !== file.id))
                }
              }}
              onDelete={() => {
                setFileToDelete(file)
                setShowDeleteConfirm(true)
              }}
              onCopyUrl={() => copyToClipboard(file.public_url)}
              deleteMutation={deleteMutation}
            />
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onFileSelect={handleFileSelect}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          dragOver={dragOver}
          uploadProgress={uploadProgress}
          isUploading={uploadMutation.isLoading}
          fileInputRef={fileInputRef}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false)
          setFileToDelete(null)
        }}
        onConfirm={() => {
          if (fileToDelete) {
            deleteMutation.mutate(fileToDelete.id)
            setShowDeleteConfirm(false)
            setFileToDelete(null)
          }
        }}
        isLoading={deleteMutation.isLoading}
        title="Delete File"
        message={`Are you sure you want to delete "${fileToDelete?.original_name}"? This action cannot be undone.`}
        confirmText="Delete File"
        confirmVariant="destructive"
      />

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={() => {
          selectedFiles.forEach(fileId => deleteMutation.mutate(fileId))
          setShowBulkDeleteConfirm(false)
        }}
        isLoading={deleteMutation.isLoading}
        title="Delete Files"
        message={`Are you sure you want to delete ${selectedFiles.length} ${selectedFiles.length === 1 ? 'file' : 'files'}? This action cannot be undone.`}
        confirmText={`Delete ${selectedFiles.length} ${selectedFiles.length === 1 ? 'File' : 'Files'}`}
        confirmVariant="destructive"
      />
    </div>
  )
}

// Media File Card Component
const MediaFileCard = ({ file, viewMode, isSelected, onSelect, onDelete, onCopyUrl, deleteMutation }) => {
  const [showActions, setShowActions] = useState(false)
  const FileIcon = file.mime_type?.startsWith('image/') ? ImageIcon :
                   file.mime_type?.startsWith('video/') ? Video : File

  const getFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  if (viewMode === 'list') {
    return (
      <Card className="hover:shadow-md transition-shadow border-neutral-200 dark:border-[#2f2b41]">
        <CardContent className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-4">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSelect(e.target.checked)}
              className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-neutral-300 rounded"
            />
            
            {file.mime_type?.startsWith('image/') ? (
              <img
                src={file.thumbnail_url || file.public_url}
                alt={file.alt_text || file.original_name}
                className="h-12 w-12 rounded object-cover"
              />
            ) : (
              <div className="h-12 w-12 rounded bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center">
                <FileIcon className="h-6 w-6 text-neutral-400" />
              </div>
            )}
            
            <div>
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-50">
                {file.original_name}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {getFileSize(file.size)} • {new Date(file.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="sm" onClick={onCopyUrl} title="Copy URL">
              <Copy className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => window.open(file.public_url, '_blank')} title="Preview">
              <Eye className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setFileToDelete(file)
                setShowDeleteConfirm(true)
              }} 
              disabled={deleteMutation.isLoading} 
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        className="relative group cursor-pointer rounded-xl border-2 transition-all duration-300 overflow-hidden shadow-sm hover:shadow-lg border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#1a1625]"
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        {/* Selection Checkbox */}
        <div className="absolute top-2 left-2 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(e.target.checked)}
            className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-neutral-300 rounded"
          />
        </div>

        {/* File Preview */}
        <div className="aspect-square p-4 flex items-center justify-center bg-neutral-50 dark:bg-[#1f1c2c] rounded-t-xl relative">
          {file.mime_type?.startsWith('image/') ? (
            <img
              src={file.thumbnail_url || file.public_url}
              alt={file.alt_text || file.original_name}
              className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
            />
          ) : (
            <FileIcon className="h-12 w-12 text-neutral-400 dark:text-neutral-500" />
          )}

          {/* Actions Overlay */}
          {showActions && (
            <div className="absolute inset-0 bg-black/60 opacity-100 transition-all duration-300 rounded-xl flex items-center justify-center">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    window.open(file.public_url, '_blank')
                  }}
                  className="bg-white/90 hover:bg-white text-neutral-700 border-white/50"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    onCopyUrl()
                  }}
                  className="bg-white/90 hover:bg-white text-neutral-700 border-white/50"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete()
                  }}
                  disabled={deleteMutation.isLoading}
                  className="bg-white/90 hover:bg-white text-error-600 border-white/50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* File Info */}
        <div className="p-4 bg-white dark:bg-[#1a1625] rounded-b-xl">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate mb-2">
            {file.original_name}
          </h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {getFileSize(file.size)}
          </p>
        </div>
      </Card>
    </motion.div>
  )
}

// Upload Modal Component
const UploadModal = ({ 
  isOpen, 
  onClose, 
  onFileSelect, 
  onDrop, 
  onDragOver, 
  onDragLeave, 
  dragOver, 
  uploadProgress, 
  isUploading,
  fileInputRef 
}) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-sm transition-all" onClick={onClose} />

        <Card className="inline-block align-bottom text-left overflow-hidden shadow-2xl border-0 transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full bg-white dark:bg-[#1a1625]">
          <CardHeader className="px-6 pt-6 pb-4">
            <div className="flex items-center justify-between mb-4">
              <CardTitle className="text-lg font-medium text-neutral-900 dark:text-neutral-50">
                Upload Files
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-6 w-6" />
              </Button>
            </div>
          </CardHeader>

          <CardContent className="px-6 pb-4">
            <div
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              className={cn(
                'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
                dragOver
                  ? 'border-accent-400 bg-accent-50 dark:bg-accent-900/20'
                  : 'border-neutral-300 dark:border-neutral-600'
              )}
            >
              <Upload className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
              <div className="space-y-2">
                <p className="text-lg font-medium text-neutral-900 dark:text-neutral-50">
                  Drop files here or use button below
                </p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Supports: Images, Videos, Documents (Max 10MB per file)
                </p>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,.pdf,.doc,.docx,.txt"
                onChange={onFileSelect}
                className="hidden"
              />
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="mt-4"
                disabled={isUploading}
              >
                Select Files
              </Button>
            </div>

            {/* Upload Progress */}
            {isUploading && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    Uploading...
                  </span>
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    {uploadProgress.uploading || 0}%
                  </span>
                </div>
                <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                  <div
                    className="bg-accent-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress.uploading || 0}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
          
          <div className="bg-neutral-50/50 dark:bg-[#1f1c2c] border-t border-neutral-200 dark:border-[#2f2b41] px-6 py-4 flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default MediaLibrary